import { html, LitElement } from 'lit';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { ApplicantsService } from '../../../services/applicants.service.js';
import { DebugService } from '../../../shared/services/debug.service.js';
import { SessionService } from '../../../core/services/session/session.service.js';
import { REFI_PERSONAL_CONST } from '../../../shared/constants/app.constants.js';
import { stratum } from '../../../../constants/stratum.js';

class RefiAddressReview extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiAddressReview');
  }

  static properties = {
    addressLine1: { type: String },
    addressLine2: { type: String },
    city: { type: String },
    stateCode: { type: String },
    zipCode: { type: String },
    experiments: { type: Object, reflect: true },
  };

  async connectedCallback() {
    super.connectedCallback();
    const eAppId = SessionService.session?.eAppId;

    try {
      await ApplicantsService.getApplicantInformation(eAppId).then(
        (response) => {
          const primaryApplicant = response.applicants.find(
            (applicant) => applicant.applicantType === 'Applicant'
          );

          if (
            !primaryApplicant.address.addressLine1 ||
            !primaryApplicant.address.city ||
            !primaryApplicant.address.stateCode ||
            !primaryApplicant.address.postalCode
          ) {
            this.debug.log('Missing applicant address field');
            stratum.publishTag(TagKey.PERSONAL_CHECKOUT_PERSONAL_MFE_LOAD, {
              replacements: {
                value: 'address mfe - failed',
              },
            });
            this.dispatchFailedToLoadOrUpdateEvent();
          } else {
            stratum.publishTag(TagKey.PERSONAL_CHECKOUT_PERSONAL_MFE_LOAD, {
              replacements: {
                value: 'address mfe - successful',
              },
            });
          }

          this.addressLine1 = primaryApplicant.address.addressLine1;
          this.addressLine2 = primaryApplicant.address.addressLine2;
          this.city = primaryApplicant.address.city;
          this.stateCode = primaryApplicant.address.stateCode;
          this.zipCode = primaryApplicant.address.postalCode;
          this.dispatchCompleteLoadingEvent();
        }
      );

      try {
        await ApplicantsService.getStips(eAppId).then((res) => {
          const residenceStip = res.find(
            (stip) =>
              stip.taskType === REFI_PERSONAL_CONST.STIP_TYPE.PROOF_OF_RESIDENCE
          );
          if (!residenceStip) {
            this.debug.log(
              'Missing Proof of Residence stip info after call to Stips API'
            );
          }
          if (
            residenceStip.taskStatus.toString().toUpperCase() ===
            REFI_PERSONAL_CONST.STIP_INCOMPLETE
          ) {
            this.dispatchEvent(
              new CustomEvent('checkout-update-mudflap', {
                bubbles: true,
                composed: true,
                detail: {
                  actionName: 'update-mudflap',
                  id: 'address-card', // 'income-card', etc.
                  text: 'You’ll upload address verification later',
                },
              })
            );
          }
          stratum.publishTag(TagKey.ADDRESS_CHECKOUT_POR_STIP_MUDFLAP_LOADS, {
            replacements: {
              value: `successful - ${residenceStip.taskStatus}`,
            },
          });
        });
      } catch (err) {
        stratum.publishTag(TagKey.ADDRESS_CHECKOUT_POR_STIP_MUDFLAP_LOADS, {
          replacements: {
            value: 'failed',
          },
        });
        this.debug.log(
          'Failed to retrieve stip information for address review on checkout: ',
          err.message
        );
      }
    } catch (err) {
      stratum.publishTag(TagKey.PERSONAL_CHECKOUT_PERSONAL_MFE_LOAD, {
        replacements: {
          value: 'address mfe - failed',
        },
      });
      this.debug.log(
        'Failed to retrieve address information for review on checkout: ',
        err.message
      );
      this.dispatchFailedToLoadOrUpdateEvent();
    }
  }

  dispatchCompleteLoadingEvent() {
    this.dispatchEvent(
      new CustomEvent('checkout-completed-loading', {
        bubbles: true,
        composed: true,
        detail: {
          id: 'address-card',
        },
      })
    );
    this.debug.log('completed loading event fired');
  }

  dispatchFailedToLoadOrUpdateEvent() {
    this.dispatchEvent(
      new CustomEvent('failed-load-or-update', {
        bubbles: true,
        composed: true,
        detail: {
          id: 'address-card',
        },
      })
    );
    this.checkout = 'error';
    this.debug.log('failed to load or update event fired');
  }

  render() {
    return this.addressLine1 && this.city && this.stateCode && this.zipCode
      ? html` <div>
          <fs-body-text variant="primary"
            >${this.generateAddressLineText()}</fs-body-text
          >
          <fs-body-text variant="primary"
            >${this.city}, ${this.stateCode} ${this.zipCode}</fs-body-text
          >
        </div>`
      : null;
  }

  generateAddressLineText() {
    return this.addressLine2 && this.addressLine2 !== ''
      ? `${this.addressLine1}, ${this.addressLine2}`
      : `${this.addressLine1}`;
  }
}

customElements.define('refi-address-review', RefiAddressReview);
